import API from "../lib/TimeEditAPI";
import { McFluffy } from "./McFluffy";
import ObjectNameCache from "../lib/ObjectNameCache";
import { Model } from "./Model";
import { Reservation } from "./Reservation";
import ReservationStatus from "../lib/ReservationStatus";
import ReservationConstants from "../lib/ReservationConstants";
import Log from "../lib/Log";
import _ from "underscore";
import { TClusterKindValue, ClusterKind } from "../lib/EntryConstants";
import { Entry } from "./Entry";
import APIError from "../lib/APIError";
import { TMcFluffyActivityData } from "../types/api/shared";

const SNAPSHOT_LIMIT = 20;

export class Selection extends Model {
    fluffy: McFluffy | null;
    editedFluffy?: McFluffy | null;
    mode: number;
    editedEntry: Entry | null;
    timeLimit: any;
    availableObjects: boolean;
    selectNextItem: boolean;
    reservations: number[];
    groups: number[];
    isGroupMode: boolean;
    length: number | false;
    clusterKind?: TClusterKindValue;
    obstacleTextTypes: number[];
    snapshots: McFluffy[];
    currentSnapshot: number;
    duration: number | false;

    static DEFAULT = 0;
    static EDIT = 1;
    static WAITING_LIST = 2;

    constructor() {
        super("Selection");
        this.fluffy = null;
        this.mode = Selection.DEFAULT;
        this.editedEntry = null;
        this.timeLimit = null;
        this.availableObjects = true;
        this.selectNextItem = false;
        this.reservations = [];
        this.groups = [];
        this.isGroupMode = false;
        this.length = false; // Holds length of a selected reservation on the waiting list
        this.obstacleTextTypes = [];
        this.snapshots = [];
        this.currentSnapshot = -1;
        this.duration = false; // Holds duration from activity manager
    }

    createFluffy(templateId, layerId, templateKind, callback) {
        let baseFluffy = McFluffy.create();
        baseFluffy.layer = layerId || 0;
        if (templateKind !== null) {
            baseFluffy.templateKind = templateKind;
        }
        if (templateId !== null) {
            baseFluffy.templateGroupId = templateId;
        }

        API.updateMcFluffy(baseFluffy.toJson(), (result) => {
            const newFluffy = McFluffy.create(result);
            callback(this.setFluffy(newFluffy));
        });
    }

    getCurrentReservationId(): number {
        return this.reservations.length > 0 ? this.reservations[0] : 0;
    }

    setLayer(layerId) {
        return this.immutableSet({
            fluffy: this.fluffy?.setLayer(layerId),
        });
    }

    setGroups(groupIds) {
        return this.immutableSet({
            groups: groupIds,
        });
    }

    enableAddMode(groupIds, keepReservations = false) {
        return this.immutableSet({
            groups: groupIds,
            isGroupMode: true,
            reservations: keepReservations ? this.reservations : [],
        });
    }

    toggleAddMode(groupIds: number[]) {
        return this.immutableSet({
            groups: groupIds,
            isGroupMode: !this.isGroupMode,
        });
    }

    clearBeginEndTimes() {
        return this.immutableSet({
            fluffy: this.fluffy?.immutableSet({
                beginTime: undefined,
                endTime: undefined,
            }),
        });
    }

    clearReservations() {
        return this.disableEditMode().immutableSet({
            reservations: [],
            fluffy: this.fluffy?.immutableSet({
                reservation: undefined,
                beginTime: undefined,
                endTime: undefined,
                activityInfo: [],
            }),
        });
    }

    belongsToGroup() {
        return this.groups.length > 0;
    }

    getGroupIds() {
        return this.groups;
    }

    setFluffy(fluffy?: McFluffy, clearReservations = this.mode === Selection.DEFAULT) {
        if (!fluffy) {
            console.warn("Cannot setFluffy. fluffy is not defined");
            return this;
        }
        const newTypes = fluffy.getTypeIds();
        const obstacleTextTypes = this.obstacleTextTypes.filter((textType) =>
            _.contains(newTypes, textType)
        );
        let snapshots = [...this.snapshots];
        const currentJson = fluffy.toJsonWithoutLayer();
        let currentSnapshot = snapshots.findIndex((snapFluffy) =>
            _.isEqual(snapFluffy.toJsonWithoutLayer(), currentJson)
        );
        if (currentSnapshot === -1) {
            // If the fluffy is not in the stack
            if (
                // If there are no snapshots, or the next fluffy isn't the latest in the stack
                snapshots.length === 0 ||
                !_.isEqual(
                    fluffy.toJsonWithoutLayer(),
                    snapshots[this.currentSnapshot].toJsonWithoutLayer()
                )
            ) {
                currentSnapshot = this.currentSnapshot + 1;
                snapshots = _.head(snapshots, currentSnapshot);
                snapshots.push(fluffy);
            } else {
                currentSnapshot = this.currentSnapshot;
            }
        }
        if (snapshots.length > SNAPSHOT_LIMIT) {
            snapshots = _.last(snapshots, SNAPSHOT_LIMIT);
            currentSnapshot = SNAPSHOT_LIMIT - 1;
        }

        let groups = this.groups;
        let reservations = this.reservations;
        if (clearReservations && !this.isGroupMode) {
            groups = [];
            reservations = [];
        }

        return this.immutableSet({
            fluffy,
            snapshots,
            currentSnapshot,
            obstacleTextTypes,
            reservations,
            groups,
        });
    }

    setDuration(duration) {
        if (!duration) {
            return this;
        }
        return this.immutableSet({
            duration,
        });
    }

    setSelectNextItem(selectNextItem) {
        return this.immutableSet({
            selectNextItem,
        });
    }

    isEditRequest() {
        return this.editedEntry?.status === ReservationStatus.C_REQUESTED.id;
    }

    isEditMode() {
        return this.mode === Selection.EDIT;
    }

    isWaitingListMode() {
        return this.mode === Selection.WAITING_LIST;
    }

    hasTimeLimit() {
        return this.timeLimit !== null;
    }

    setTimeLimit(begin, end) {
        let limit = { begin, end };
        if (!end) {
            limit = getTimeLimit(begin);
        }
        return this.immutableSet({
            timeLimit: limit,
        });
    }

    removeTimeLimit() {
        return this.immutableSet({
            timeLimit: null,
        });
    }

    setAvailableObjects(availableObjects) {
        return this.immutableSet({ availableObjects });
    }

    enableEditMode(editedEntry, isGroupMode = false) {
        if (!editedEntry) {
            return this;
        }
        const iGM = isGroupMode || this.isGroupMode;
        return this.immutableSet({
            mode: Selection.EDIT,
            editedEntry,
            editedFluffy: this.fluffy,
            timeLimit: getTimeLimit(editedEntry),
            availableObjects: true,
            isGroupMode: iGM,
            groups: iGM ? this.groups : editedEntry.groups, // If already in group mode, keep those groups
        });
    }

    disableEditMode() {
        return this.immutableSet({
            mode: Selection.DEFAULT,
            editedEntry: null,
            editedFluffy: null,
            timeLimit: null,
        });
    }

    disableWaitingListMode() {
        return this.immutableSet({
            mode: Selection.DEFAULT,
            length: false,
        });
    }

    finishEditMode(
        closeRequest,
        allowAvailabilityOverlap = false,
        successCallback: (reservationIds: number[]) => void = _.noop as any,
        errorCallback: (e: Error) => void = _.noop as any,
        useAsyncSave = true,
        activityInfo: TMcFluffyActivityData[] = []
    ) {
        let saveFluffy = this.fluffy;
        if (closeRequest && this.isEditRequest()) {
            saveFluffy = McFluffy.create(saveFluffy?.toJson());
            saveFluffy.status = [ReservationStatus.C_REJECTED.id];
        } else {
            // Always allow incomplete reservations in edit mode
            saveFluffy = saveFluffy!.immutableSet({ allowIncomplete: true });
        }
        if (activityInfo && activityInfo.length > 0) {
            saveFluffy = saveFluffy.setActivityInfo(activityInfo);
        }
        Reservation.save(
            this.editedEntry,
            saveFluffy,
            (result, reservationIds) => {
                if (result instanceof Error) {
                    if (
                        (result as any).code !==
                        ReservationConstants.ERROR_AVAILABILITY_OVERLAP_POSSIBLE
                    ) {
                        Log.warning(result.message, {
                            details: (result as APIError).referenceItems || [],
                        });
                    }
                    errorCallback(result);
                } else {
                    successCallback(reservationIds);
                }
            },
            true,
            allowAvailabilityOverlap, // Allow availability overlap
            false,
            useAsyncSave
        );
    }

    setFluffyFields(fields) {
        return this.setFluffy(this.fluffy!.setFields(fields), false);
    }

    isObstacleTextType(typeId: number) {
        return _.contains(this.obstacleTextTypes, typeId);
    }

    addObstacleTextType(typeId: number) {
        return this.immutableSet({ obstacleTextTypes: this.obstacleTextTypes.concat(typeId) });
    }

    removeObstacleTextType(typeId) {
        return this.immutableSet({
            obstacleTextTypes: this.obstacleTextTypes.filter((id) => id !== typeId),
        });
    }

    setReservation(
        inIds: number[],
        callback,
        modify = false,
        isWaitingList = false,
        length: number | false = false,
        isNewReservation = false,
        clusterKind: TClusterKindValue = ClusterKind.NONE
    ) {
        const reservationIds: number[] = _.asArray(inIds) as any;
        if (reservationIds.length === 0) {
            return callback(this, true);
        }
        let resultSetReservationToMcFluffy: any = false;
        let resultGetReservations: any = false;
        API.getTemplateGroups(this.fluffy?.templateKind.number, (templateGroups) => {
            const templateGroupIds = isWaitingList
                ? [0]
                : templateGroups.parameters[0].map((group) => group.id);

            _.runAsync(
                [
                    (done) => {
                        const inFluffy = this.fluffy?.toJson();
                        if (!inFluffy) {
                            done();
                            return;
                        }
                        if (inFluffy.reservation && !this.isEditMode()) {
                            delete inFluffy.reservation;
                        }
                        API.setReservationToMcFluffy(
                            reservationIds,
                            inFluffy,
                            modify,
                            templateGroupIds,
                            isNewReservation,
                            (result) => {
                                resultSetReservationToMcFluffy = result;
                                done();
                            }
                        );
                    },
                    (done) => {
                        API.getReservations(reservationIds, (reservations) => {
                            resultGetReservations = reservations;
                            done();
                        });
                    },
                ],
                () => {
                    if (
                        resultSetReservationToMcFluffy.parameters[0] === null ||
                        reservationIds.length === 0 ||
                        resultGetReservations.length === 0
                    ) {
                        // We get no fluffy if we asked for a non-existant reservation ID
                        return callback(this, false);
                    }
                    if (modify && !resultSetReservationToMcFluffy.parameters[1]) {
                        return callback(this, false);
                    }
                    const reservationObjects = (resultGetReservations[0]?.objects ?? []).filter(
                        (obj) => obj.id !== 0 && obj.fields && obj.fields.length > 0
                    );
                    ObjectNameCache.setMany(
                        reservationObjects.map((obj) => ({
                            typeId: obj.type.id,
                            objectId: obj.id,
                            name: obj.fields[0].values.join(", "),
                        }))
                    );
                    const fluffy = McFluffy.create(resultSetReservationToMcFluffy.parameters[0]);
                    fluffy.rules = resultSetReservationToMcFluffy.parameters[2];
                    let newSelection = this.setFluffy(fluffy, false);
                    const mode = isWaitingList ? Selection.WAITING_LIST : Selection.DEFAULT;
                    newSelection = newSelection.immutableSet({ mode, length, clusterKind });
                    if (isNewReservation && !this.isGroupMode) {
                        newSelection = newSelection.setGroups([]);
                    }
                    return callback(
                        newSelection.immutableSet({ reservations: reservationIds }),
                        resultSetReservationToMcFluffy.parameters[1]
                    );
                }
            );
        });
        return null;
    }

    hasSnapshots(hasPrevious: boolean): boolean {
        if (hasPrevious === true) {
            return this.currentSnapshot > 0;
        }

        return this.currentSnapshot < this.snapshots.length - 1;
    }

    getNextSnapshot() {
        return this.snapshots[this.currentSnapshot + 1];
    }

    getPreviousSnapshot() {
        return this.snapshots[this.currentSnapshot - 1];
    }
}

const getTimeLimit = function (entry: Entry) {
    const beginTimes = entry.startTimes.map((time) => time.getMts());
    const endTimes = entry.endTimes.map((time) => time.getMts());
    const sortedBeginTimes = [...beginTimes];
    sortedBeginTimes.sort();
    const sortedEndTimes = [...endTimes];
    sortedEndTimes.sort();
    return {
        begin: beginTimes,
        end: endTimes,
        first: sortedBeginTimes[0],
        last: sortedEndTimes[sortedEndTimes.length - 1],
    };
};
